/**
 * Interactive edit rig for a previewed floor level's box.
 *
 * - The four VERTICAL faces carry horizontal drag handles: dragging one moves
 *   that side of the footprint in/out along its outward normal (camera orbit
 *   is blocked for the duration of the gesture).
 * - The standard transform gizmo (TransformGizmoInteraction +
 *   FloorFootprintGizmoBinding) floats at the footprint centre with a
 *   2D-only handle set: XY translation, Z rotation and XY scale about the
 *   centroid — full control over the (possibly rotated) footprint rectangle.
 * - Clicking the TOP or BOTTOM face enters a plane-repick mode: the same
 *   surface-fitted plane tool as ManualFloorTool follows the cursor, a click
 *   commits the new ceiling (top) / floor (bottom) height, and a right-click
 *   or Escape cancels. While repicking, the owner extends the isolation box
 *   beyond the picked face (FACE_REPICK_EXTRUDE_M) so the true surface is
 *   visible.
 *
 * The rig owns only geometry and gestures; the owning tool applies isolation,
 * mutates the level data and persists via the callbacks.
 */

import * as THREE from 'three';
import { Viewer } from '../../../viewer/viewer';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { PlaneCreator } from './PlaneCreator';
import { DefaultColors } from '../../../defines';
import type { DragEventData } from '../../BaseTool';
import { TransformGizmoInteraction } from './transformGizmo/TransformGizmoInteraction';
import { FloorFootprintGizmoBinding } from './transformGizmo/FloorFootprintGizmoBinding';

export type FloorRigFace = 'top' | 'bottom';

export interface FloorPreviewEditRigCallbacks {
  /** Footprint changed via a side-handle drag; `committed` on release. */
  onFootprintChanged(corners: THREE.Vector2[], committed: boolean): void;
  /** A new floor ('bottom') or ceiling ('top') plane height was picked. */
  onFacePlanePicked(face: FloorRigFace, z: number): void;
  /** Repick mode entered (face) or exited (null) — owner adjusts isolation. */
  onRepickChanged(face: FloorRigFace | null): void;
}

/** How far the isolation box extends past a face while repicking its plane. */
export const FACE_REPICK_EXTRUDE_M = 1.5;

/**
 * A face click counts as clicking THAT surface when the visible cloud point
 * under the cursor is within this of the face's z-plane (surface scatter,
 * skirting, floor clutter). A cloud hit farther from the plane means the user
 * aimed at a wall / the interior — the click belongs to the host tool.
 */
export const FACE_PICK_SURFACE_TOLERANCE_M = 0.35;

const HANDLE_COLOR = 0x59cd90;
const HANDLE_HOVER_COLOR = 0xb5ffa1;
const FACE_OPACITY = 0.05;
const FACE_HOVER_OPACITY = 0.14;
/** A side can never be dragged closer than this to its opposite side. */
const MIN_FOOTPRINT_EXTENT_M = 1.0;

interface SideDragState {
  edge: number;
  outwardNormal: THREE.Vector2;
  startCorners: THREE.Vector2[];
  /** Drag-start hit on the horizontal plane at handle height. */
  startPoint: THREE.Vector3;
  planeZ: number;
}


export class FloorPreviewEditRig {
  private readonly viewer: Viewer;
  private readonly callbacks: FloorPreviewEditRigCallbacks;

  private corners: THREE.Vector2[] = [];
  private floorZ = 0;
  private ceilingZ = 0;
  private withOutline = false;

  private group: THREE.Group | null = null;
  private handleMeshes: THREE.Mesh[] = [];
  private faceMeshes: { top: THREE.Mesh; bottom: THREE.Mesh } | null = null;
  private hoveredHandle = -1;
  private hoveredFace: FloorRigFace | null = null;

  private pressedHandle = -1;
  private dragState: SideDragState | null = null;
  private orbitBlocked = false;

  // Standard transform gizmo (XY move / Z rotate / XY scale) over the
  // footprint, hosted through the shared TransformGizmoInteraction.
  private gizmoInteraction: TransformGizmoInteraction | null = null;
  private footprintBinding: FloorFootprintGizmoBinding | null = null;

  // Plane-repick sub-mode (ManualFloorTool-style surface picking)
  private repickFaceState: FloorRigFace | null = null;
  private pickScene: THREE.Scene | null = null;
  private activePlane: PointoramaPlane | null = null;
  private planeCreator: PlaneCreator | null = null;
  private unregisterOverlay: (() => void) | null = null;

  private readonly raycaster = new THREE.Raycaster();

  constructor(viewer: Viewer, callbacks: FloorPreviewEditRigCallbacks) {
    this.viewer = viewer;
    this.callbacks = callbacks;
  }

  get repickFace(): FloorRigFace | null {
    return this.repickFaceState;
  }

  /**
   * Build (or rebuild) the rig for the given footprint and z-range.
   * `outline` draws the box edges too — for hosts without their own preview
   * outline (ManualFloorTool).
   */
  show(corners: THREE.Vector2[], floorZ: number, ceilingZ: number, options?: { outline?: boolean }): void {
    this.corners = corners.map((c) => c.clone());
    this.floorZ = floorZ;
    this.ceilingZ = ceilingZ;
    if (options?.outline !== undefined) this.withOutline = options.outline;
    this.rebuildMeshes();
    this.ensureGizmo().attach();
  }

  /** Lazily build the footprint transform gizmo hosting. */
  private ensureGizmo(): TransformGizmoInteraction {
    if (this.gizmoInteraction) return this.gizmoInteraction;
    this.footprintBinding = new FloorFootprintGizmoBinding({
      getCorners: () => this.corners,
      getMidZ: () => (this.floorZ + this.ceilingZ) / 2,
      apply: (corners) => {
        this.corners = corners.map((c) => c.clone());
        this.rebuildMeshes();
        this.callbacks.onFootprintChanged(
          this.corners.map((c) => c.clone()),
          false,
        );
      },
      commit: () => {
        this.callbacks.onFootprintChanged(
          this.corners.map((c) => c.clone()),
          true,
        );
      },
      minExtent: MIN_FOOTPRINT_EXTENT_M,
    });
    this.gizmoInteraction = new TransformGizmoInteraction({
      viewer: this.viewer,
      // Footprint edits persist through the rig's reactive-property flow
      // (like the side handles) — no tool-local undo snapshot to push.
      undoRedo: () => null,
      resolveBinding: () => (this.group !== null && this.corners.length >= 4 && this.repickFaceState === null ? this.footprintBinding : null),
    });
    return this.gizmoInteraction;
  }

  /** Remove the rig (and any active repick) from the scene. */
  hide(): void {
    this.cancelRepick();
    this.releaseOrbitBlock();
    this.pressedHandle = -1;
    this.dragState = null;
    this.gizmoInteraction?.detach();
    this.disposeGroup();
  }

  dispose(): void {
    this.hide();
    this.gizmoInteraction?.dispose();
    this.gizmoInteraction = null;
  }

  // ─── Geometry ──────────────────────────────────────────────────────────

  private disposeGroup(): void {
    if (!this.group) return;
    this.viewer.measurementScene.remove(this.group);
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) child.material.dispose();
      }
    });
    this.group = null;
    this.handleMeshes = [];
    this.faceMeshes = null;
  }

  private centroid(): THREE.Vector2 {
    const c = new THREE.Vector2();
    for (const corner of this.corners) c.add(corner);
    return c.multiplyScalar(1 / this.corners.length);
  }

  /** Outward unit normal of edge i (corners[i] → corners[i+1]). */
  private edgeOutwardNormal(edge: number): THREE.Vector2 {
    const a = this.corners[edge];
    const b = this.corners[(edge + 1) % this.corners.length];
    const normal = new THREE.Vector2(b.y - a.y, -(b.x - a.x)).normalize();
    const mid = new THREE.Vector2().addVectors(a, b).multiplyScalar(0.5);
    if (normal.dot(mid.clone().sub(this.centroid())) < 0) normal.negate();
    return normal;
  }

  private rebuildMeshes(): void {
    this.disposeGroup();
    if (this.corners.length < 4) return;

    const group = new THREE.Group();
    group.name = 'floor-preview-edit-rig';

    // Side handles at mid-height of each vertical face: a double-headed
    // arrow (↔) along the face's outward normal — the horizontal resize
    // affordance. Each sub-mesh carries the handle index for picking.
    const midZ = (this.floorZ + this.ceilingZ) / 2;
    for (let i = 0; i < 4; i++) {
      const a = this.corners[i];
      const b = this.corners[(i + 1) % 4];
      const mid = new THREE.Vector2().addVectors(a, b).multiplyScalar(0.5);
      const normal = this.edgeOutwardNormal(i);

      const handleGroup = new THREE.Group();
      handleGroup.position.set(mid.x, mid.y, midZ);
      // Local +X = outward normal
      handleGroup.rotation.z = Math.atan2(normal.y, normal.x);

      const makeHandleMesh = (geometry: THREE.BufferGeometry): THREE.Mesh => {
        const material = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR, transparent: true, opacity: 0.95, depthTest: false });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = 1000;
        mesh.userData = { rigHandle: i };
        this.handleMeshes.push(mesh);
        handleGroup.add(mesh);
        return mesh;
      };

      // Shaft along local X, plus a cone head on each end
      makeHandleMesh(new THREE.BoxGeometry(0.55, 0.07, 0.07));
      const headOut = makeHandleMesh(new THREE.ConeGeometry(0.11, 0.24, 12));
      headOut.rotation.z = -Math.PI / 2; // cone +Y → local +X (outward)
      headOut.position.x = 0.4;
      const headIn = makeHandleMesh(new THREE.ConeGeometry(0.11, 0.24, 12));
      headIn.rotation.z = Math.PI / 2; // local −X (inward)
      headIn.position.x = -0.4;

      group.add(handleGroup);
    }

    // Top/bottom faces: barely visible but pickable quads
    const makeFace = (z: number, face: FloorRigFace): THREE.Mesh => {
      const positions = new Float32Array([
        this.corners[0].x, this.corners[0].y, z,
        this.corners[1].x, this.corners[1].y, z,
        this.corners[2].x, this.corners[2].y, z,
        this.corners[0].x, this.corners[0].y, z,
        this.corners[2].x, this.corners[2].y, z,
        this.corners[3].x, this.corners[3].y, z,
      ]);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      // depthTest off so the hover highlight reads IN FRONT of the cloud
      const material = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR, transparent: true, opacity: FACE_OPACITY, side: THREE.DoubleSide, depthWrite: false, depthTest: false });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.renderOrder = 999;
      mesh.userData = { rigFace: face };
      group.add(mesh);
      return mesh;
    };
    this.faceMeshes = { top: makeFace(this.ceilingZ, 'top'), bottom: makeFace(this.floorZ, 'bottom') };

    // Optional box edges (highlight for hosts without their own outline)
    if (this.withOutline) {
      const pts: number[] = [];
      const push = (a: THREE.Vector2, az: number, b: THREE.Vector2, bz: number): void => {
        pts.push(a.x, a.y, az, b.x, b.y, bz);
      };
      for (let i = 0; i < 4; i++) {
        const a = this.corners[i];
        const b = this.corners[(i + 1) % 4];
        push(a, this.floorZ, b, this.floorZ);
        push(a, this.ceilingZ, b, this.ceilingZ);
        push(a, this.floorZ, a, this.ceilingZ);
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
      const outline = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: HANDLE_COLOR, transparent: true, opacity: 0.9, depthTest: false }));
      outline.renderOrder = 998;
      group.add(outline);
    }

    // While repicking a plane, the rig's own visuals (arrows, faces,
    // outline) are hidden — only the pick-plane preview should be in view.
    group.visible = this.repickFaceState === null;

    this.group = group;
    this.viewer.measurementScene.add(group);
  }

  // ─── Raycasting ────────────────────────────────────────────────────────

  private setRaycasterFromEvent(event: MouseEvent): void {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.viewer.scene.getActiveCamera());
  }

  private pickHandle(event: MouseEvent): number {
    if (this.handleMeshes.length === 0) return -1;
    this.setRaycasterFromEvent(event);
    const hits = this.raycaster.intersectObjects(this.handleMeshes, false);
    return hits.length > 0 ? (hits[0].object.userData.rigHandle as number) : -1;
  }

  /** Face under the cursor with its ray distance, for owner-side precedence checks. */
  pickFace(event: MouseEvent): { face: FloorRigFace; distance: number } | null {
    if (!this.faceMeshes) return null;
    this.setRaycasterFromEvent(event);
    const hits = this.raycaster.intersectObjects([this.faceMeshes.top, this.faceMeshes.bottom], false);
    if (hits.length === 0) return null;
    return { face: hits[0].object.userData.rigFace as FloorRigFace, distance: hits[0].distance };
  }

  /**
   * Which face a click means to edit. Any click landing on the box (a quad
   * under the cursor) enters edit mode — a selected floor IS edit mode; the
   * host tools do their own thing only outside the box. The visible cloud
   * point refines WHICH face: near the floor plane edits the bottom, near the
   * ceiling the top (regardless of which quad the ray crossed first — from
   * above the top quad is always in front). Otherwise the nearest quad
   * decides. Null = the click missed the box entirely.
   */
  resolveFacePick(event: MouseEvent, cloudZ: number | null): FloorRigFace | null {
    if (this.corners.length < 4) return null;
    const quad = this.pickFace(event);
    if (!quad) return null;
    if (cloudZ !== null) {
      if (Math.abs(cloudZ - this.floorZ) <= FACE_PICK_SURFACE_TOLERANCE_M) return 'bottom';
      if (Math.abs(cloudZ - this.ceilingZ) <= FACE_PICK_SURFACE_TOLERANCE_M) return 'top';
    }
    return quad.face;
  }

  /** Ray ∩ horizontal plane at z, or null when parallel. */
  private hitHorizontalPlane(event: MouseEvent, z: number): THREE.Vector3 | null {
    this.setRaycasterFromEvent(event);
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -z);
    const target = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(plane, target);
  }

  // ─── Gestures (called by the owning tool; true = consumed) ─────────────

  onMouseDown(event: MouseEvent): boolean {
    if (this.repickFaceState) return false;
    if (this.gizmoInteraction?.handleMouseDown(event)) return true;
    const handle = this.pickHandle(event);
    if (handle === -1) return false;
    this.pressedHandle = handle;
    this.blockOrbit();
    return true;
  }

  onDragStart(data: DragEventData): boolean {
    if (this.gizmoInteraction?.handleDragStart(data)) return true;
    if (this.pressedHandle === -1) return false;
    const planeZ = (this.floorZ + this.ceilingZ) / 2;
    const startPoint = this.hitHorizontalPlane(data.event, planeZ);
    if (!startPoint) {
      this.pressedHandle = -1;
      this.releaseOrbitBlock();
      return false;
    }
    this.dragState = {
      edge: this.pressedHandle,
      outwardNormal: this.edgeOutwardNormal(this.pressedHandle),
      startCorners: this.corners.map((c) => c.clone()),
      startPoint,
      planeZ,
    };
    return true;
  }

  onDrag(data: DragEventData): boolean {
    if (this.gizmoInteraction?.handleDrag(data)) return true;
    const drag = this.dragState;
    if (!drag) return false;
    const current = this.hitHorizontalPlane(data.event, drag.planeZ);
    if (!current) return true;

    let delta = (current.x - drag.startPoint.x) * drag.outwardNormal.x + (current.y - drag.startPoint.y) * drag.outwardNormal.y;

    // Clamp: the dragged side may not come closer than MIN_FOOTPRINT_EXTENT_M
    // to the opposite side.
    const a = drag.startCorners[drag.edge];
    const opposite = drag.startCorners[(drag.edge + 2) % 4];
    const extent = (a.x - opposite.x) * drag.outwardNormal.x + (a.y - opposite.y) * drag.outwardNormal.y;
    delta = Math.max(delta, MIN_FOOTPRINT_EXTENT_M - extent);

    const next = drag.startCorners.map((c) => c.clone());
    const offset = drag.outwardNormal.clone().multiplyScalar(delta);
    next[drag.edge].add(offset);
    next[(drag.edge + 1) % 4].add(offset);

    this.corners = next;
    this.rebuildMeshes();
    this.callbacks.onFootprintChanged(this.corners.map((c) => c.clone()), false);
    return true;
  }

  onDragEnd(data: DragEventData): boolean {
    if (this.gizmoInteraction?.handleDragEnd(data)) return true;
    if (this.pressedHandle === -1 && !this.dragState) return false;
    const hadDrag = this.dragState !== null;
    this.pressedHandle = -1;
    this.dragState = null;
    this.releaseOrbitBlock();
    if (hadDrag) {
      this.callbacks.onFootprintChanged(
        this.corners.map((c) => c.clone()),
        true,
      );
    }
    return hadDrag;
  }

  /**
   * Click handling: releases a pressed-but-not-dragged handle, or commits the
   * repick plane. Face clicks are NOT handled here — the owner tests them via
   * `pickFace` so it can give a closer point-cloud hit precedence.
   */
  onClick(event: MouseEvent): boolean {
    if (this.gizmoInteraction?.handleClick(event)) return true;
    if (this.pressedHandle !== -1 && !this.dragState) {
      this.pressedHandle = -1;
      this.releaseOrbitBlock();
      return true;
    }
    if (this.repickFaceState) {
      this.commitRepickClick(event);
      return true;
    }
    return false;
  }

  /** Cursor-follow for the repick plane preview + handle/face hover styling. */
  onMouseMove(event: MouseEvent): boolean {
    if (this.repickFaceState && this.planeCreator) {
      const rect = this.viewer.renderer.domElement.getBoundingClientRect();
      this.planeCreator.adjustActivePlaneToMousePosition(new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top));
      return true;
    }

    const overGizmo = this.gizmoInteraction?.handleMouseMove(event) ?? false;

    const handle = overGizmo ? -1 : this.pickHandle(event);
    if (handle !== this.hoveredHandle) {
      this.hoveredHandle = handle;
      for (const mesh of this.handleMeshes) {
        if (mesh.material instanceof THREE.MeshBasicMaterial) mesh.material.color.set((mesh.userData.rigHandle as number) === handle ? HANDLE_HOVER_COLOR : HANDLE_COLOR);
      }
    }

    const face = handle === -1 && !overGizmo ? (this.pickFace(event)?.face ?? null) : null;
    if (face !== this.hoveredFace) {
      this.hoveredFace = face;
      if (this.faceMeshes) {
        for (const [name, mesh] of Object.entries(this.faceMeshes)) {
          if (mesh.material instanceof THREE.MeshBasicMaterial) mesh.material.opacity = name === face ? FACE_HOVER_OPACITY : FACE_OPACITY;
        }
      }
    }

    return handle !== -1 || overGizmo;
  }

  /** Right-click cancels an active repick. Returns true when consumed. */
  onRightClick(_event: MouseEvent): boolean {
    return this.cancelRepick();
  }

  // ─── Plane repick sub-mode ─────────────────────────────────────────────

  enterRepick(face: FloorRigFace): void {
    if (this.repickFaceState) this.cancelRepick();
    this.repickFaceState = face;

    if (!this.pickScene) {
      this.pickScene = new THREE.Scene();
      this.pickScene.name = 'floor-preview-repick-scene';
      this.viewer.inputHandler.registerInteractiveScene(this.pickScene);
    }

    // Fresh plane per session — a finished plane can't be reused.
    this.activePlane = new PointoramaPlane();
    this.activePlane.tolerance = 0.01;
    this.activePlane.visible = false;
    this.activePlane.color = DefaultColors.OBJECTS_THREE;
    this.pickScene.add(this.activePlane);

    if (!this.planeCreator) {
      this.planeCreator = new PlaneCreator(this.viewer, this.activePlane);
      this.pickScene.add(this.planeCreator);
    } else {
      this.planeCreator.activePlane = this.activePlane;
    }

    this.unregisterOverlay = this.viewer.registerToolOverlayScene(this.pickScene);

    // Clean view for picking: hide the rig's own visuals for the duration.
    if (this.group) this.group.visible = false;

    this.callbacks.onRepickChanged(face);
  }

  /** Exit repick without committing. Returns true when a repick was active. */
  cancelRepick(): boolean {
    if (!this.repickFaceState) return false;
    this.teardownRepick();
    this.callbacks.onRepickChanged(null);
    return true;
  }

  private commitRepickClick(event: MouseEvent): void {
    const face = this.repickFaceState;
    if (!face || !this.planeCreator || !this.activePlane) return;

    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.planeCreator.addMarker(new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top));
    if (this.planeCreator.selectedFittingPoints.length === 0) return; // no surface under cursor — stay in repick

    this.activePlane.finish();
    const z = this.activePlane.position.z;

    this.teardownRepick();
    this.callbacks.onRepickChanged(null);
    this.callbacks.onFacePlanePicked(face, z);
  }

  private teardownRepick(): void {
    this.repickFaceState = null;
    if (this.group) this.group.visible = true;
    this.planeCreator?.reset();
    if (this.planeCreator) this.planeCreator.angleLabel.visible = false;
    if (this.activePlane && this.pickScene) {
      this.activePlane.visible = false;
      this.pickScene.remove(this.activePlane);
      this.activePlane = null;
    }
    this.unregisterOverlay?.();
    this.unregisterOverlay = null;
  }

  // ─── Orbit blocking ────────────────────────────────────────────────────

  private blockOrbit(): void {
    if (this.orbitBlocked) return;
    this.orbitBlocked = true;
    this.viewer.inputHandler?.blockOrbitControls?.();
  }

  private releaseOrbitBlock(): void {
    if (!this.orbitBlocked) return;
    this.orbitBlocked = false;
    this.viewer.inputHandler?.unblockOrbitControls?.();
  }
}
